import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useMemo } from 'react';
import { WebPage, WithContext } from 'schema-dts';
import { RailsThumbnailItem } from '~/typings/services/rails/item';
import { RailsLander } from '~/typings/services/rails/landers';
import absoluteUrl from '~/utils/absoluteUrl';

interface Props {
  lander?: RailsLander;
  items: RailsThumbnailItem[];
}

const WebPageItemsSchema: React.FC<Props> = ({ items, lander }) => {
  const router = useRouter();

  const schemaString = useMemo(() => {
    const schema: WithContext<WebPage> = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: lander?.name,
      url: lander?.canonical_url || absoluteUrl(router.asPath),
      mainEntity: {
        '@type': 'WebPageElement',
        offers: items.map(item => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: item.name,
            image: item.primary_image?.large_url,
            url: item.url,
            offers: [
              {
                '@type': 'Offer',
                priceCurrency: 'USD',
                price: item.price,
                itemCondition: item.condition_detail
                  ? item.condition_detail.slug == 'new'
                    ? 'https://schema.org/NewCondition'
                    : 'https://schema.org/UsedCondition'
                  : undefined,
              },
            ],
          },
        })),
      },
    };

    return JSON.stringify(schema);
  }, [items]);

  if (!schemaString) {
    return null;
  }

  return (
    <Script
      id="items-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: schemaString,
      }}
    />
  );
};

export default WebPageItemsSchema;
